/**
 * Hyundai Vehicle Configurator — Frontend v1.9
 *
 * Changes in v1.9:
 *  - This configurator has no sort control of its own (that's the new
 *    [hss_video_sort] shortcode) but currentState() now reads whatever
 *    sort is currently in the URL (HSSCore.parseUrlState(config).sort)
 *    and carries it into Submit, so applying a Year/Model/Trim selection
 *    no longer silently drops an already-applied sort — same fix as the
 *    chip bar's hss-frontend.js v1.12. Clear All picks up the fix for
 *    free: it already routes through HSSCore.buildClearFilterUrl(), which
 *    now strips the sort URL param too (see hss-core.js v1.8).
 *
 * Changes in v1.8.1:
 *  - Uniform placeholders across the four dropdowns: "Select Year",
 *    "Select Model", "Select Trim", "Feature Category" (Feature Category
 *    stays bare — it's an independent facet, not a required step in the
 *    Y/M/T chain, so it doesn't get the "Select …" verb). Previously these
 *    were an inconsistent mix ("Year", "Select Model", "Trim"). The
 *    disabled-state lock messages ("Select Year first" / "Select Model
 *    first") are a separate, deliberately different bit of copy — they're
 *    explaining why a field can't be touched yet, not just labeling it —
 *    and are unchanged.
 *
 * Changes in v1.8:
 *  - Model and Trim now stay genuinely locked until the level above is
 *    picked, instead of listing every Model / every Trim across the whole
 *    tree the moment the page loads. Model starts disabled with a "Select
 *    Year first" placeholder; Trim starts disabled with "Select Model
 *    first". Only the chip search bar keeps the old "show everything,
 *    narrow contextually" openness — the configurator's four plain
 *    dropdowns get overwhelming with that much exposed at once (see admin
 *    settings page, Sequential Selects card). Feature Category is
 *    unaffected — it's an independent facet, not part of the Y/M/T chain.
 *
 * Changes in v1.7:
 *  - Same fix as the chip bar's hss-frontend.js v1.8.10: the
 *    [hyundai_vehicle_configurator search_page="…"] override was parsed but
 *    never read — hssConfiguratorConfig is localized globally before this
 *    shortcode's atts exist. Boot now reads data-search-page off
 *    #hss-config-wrap and overrides searchPage for this instance only when
 *    present; the admin settings URL remains the default otherwise.
 *
 * Changes in v1.6:
 *  - Added a mobile-only accordion: a Show/Hide toggle button collapses
 *    .hss-config-body (everything except the toggle itself) so the
 *    configurator doesn't eat the whole screen on small viewports.
 *    Starts collapsed; Submit auto-collapses it again so applied filters
 *    hand space back to the results. No effect above the 767px breakpoint
 *    — see hss-configurator.css.
 *
 * Changes in v1.5:
 *  - Clear All targeted config.searchPage (the results page) as the
 *    reload target, which sent the user there even when the configurator
 *    was embedded somewhere else entirely. Now reloads whatever page
 *    it's actually on.
 *
 * Changes in v1.4:
 *  - Clear All and Submit now sync/clear the Feature Category cookie
 *    (HSSCore.syncFeatureCookie/clearFeatureCookie) — FWPVFM's clear
 *    endpoint and cookie only ever touch year/model/trim.
 *
 * Changes in v1.3:
 *  - Clear All now hands cookie-clearing off to FWPVFM's own
 *    ?_clear_vehicle_filter=1 handler (HSSCore.buildClearFilterUrl)
 *    instead of deleting the cookie client-side ourselves — atomic,
 *    server-side, before any HTML ships, and one less thing to keep in
 *    sync with FWPVFM's own logic. Requires FWPVFM 1.0.1+ (adds
 *    clearParam to its localized config).
 *  - Submit no longer calls HSSCore.syncFWPVFMCookie() — reviewed against
 *    FWPVFM's actual source; it already syncs itself off the URL our
 *    navigate() updates, so this was redundant.
 *
 * Changes in v1.2:
 *  - This feed is placed globally (top of every page), so most pages have
 *    no _model_filter in their own URL. Init now uses
 *    HSSCore.resolveInitialState() (URL-first, FWPVFM-cookie-fallback)
 *    instead of reading the URL directly — same change as the chip bar.
 *
 * Changes in v1.1:
 *  - Clear All was leaving Model/Trim visually populated: fillSelect()
 *    deliberately restores a select's old value if it's still present in
 *    the repopulated option list (correct for a normal Year/Model cascade,
 *    wrong for Clear All). Now explicitly blanks all four selects.
 *  - Clear All never called into FacetWP/HSSCore at all, so the URL and
 *    the FWP Vehicle Filter Memory cookie were both left stale. Clear All
 *    now clears that cookie directly (HSSCore.clearFWPVFMCookie) and does
 *    a hard refresh to the clean base URL — matching what the chip bar's
 *    own "×" clear button does, just via a full reload instead of the
 *    soft FWP.fetch() path, per explicit request.
 *  - Submit now also syncs the FWPVFM cookie (HSSCore.syncFWPVFMCookie)
 *    before navigating, for parity with the chip bar — same underlying
 *    gap: FWPVFM's own 'facetwp-loaded' listener never fires because
 *    FacetWP triggers that event through jQuery, not native DOM events.
 *
 * Alternate, dropdown-based visual UI for the same underlying filter model
 * as hss-frontend.js's chip bar: Year → Model → Trim (cascading), plus an
 * independent Feature Category select. Owns no data or FacetWP logic of
 * its own — everything reusable lives in window.HSSCore (assets/js/hss-core.js)
 * and is shared with the chip bar rather than reimplemented here.
 *
 * On load, restores whatever state is already in the URL (e.g. arriving via
 * a shared link like ?_model_filter=2026,elantra-2026&_feature_category=driving)
 * so the dropdowns reflect the current filters rather than resetting them.
 */
(function () {
	'use strict';

	document.addEventListener('DOMContentLoaded', function () {
		if (typeof hssConfiguratorConfig === 'undefined' ||
		    typeof hssTree === 'undefined' ||
		    typeof window.HSSCore === 'undefined') {
			return;
		}

		var config = hssConfiguratorConfig;
		var flat   = HSSCore.buildFlat(hssTree, window.hssFeatureCategories);

		var $year    = document.getElementById('hss-cfg-year');
		var $model   = document.getElementById('hss-cfg-model');
		var $trim    = document.getElementById('hss-cfg-trim');
		var $feature = document.getElementById('hss-cfg-feature');
		var $clear   = document.getElementById('hss-config-clear');
		var $submit  = document.getElementById('hss-config-submit');
		var $wrap    = document.getElementById('hss-config-wrap');
		var $toggle  = document.getElementById('hss-config-toggle');
		var $toggleLabel = document.getElementById('hss-config-toggle-label');

		// Same fix as hss-frontend.js's chip bar: hssConfiguratorConfig.searchPage
		// is localized once, globally, from the admin settings URL, before this
		// shortcode's 'search_page' attribute exists. configurator.php prints
		// that override as data-search-page on #hss-config-wrap when given —
		// apply it here, on a copy, so the admin-settings value stays the
		// default whenever no override is present.
		if ($wrap && $wrap.dataset.searchPage) {
			config = Object.assign({}, config, { searchPage: $wrap.dataset.searchPage });
		}

		if (!$year || !$model || !$trim || !$feature) return;

		/* ── Mobile accordion toggle ──────────────────────────────────────
		   The toggle button only renders visibly under the 767px breakpoint
		   (see hss-configurator.css); on desktop .hss-config-body is always
		   visible regardless of the is-collapsed class, so this is harmless
		   to run unconditionally.
		   Expanded is the default (see hss-configurator.css v1.2) —
		   is-collapsed is what hides it, added below only when a selection
		   already exists on load, or after Submit. */
		function setExpanded(expanded) {
			if (!$wrap) return;
			$wrap.classList.toggle('is-collapsed', !expanded);
			if ($toggle) $toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
			if ($toggleLabel) $toggleLabel.textContent = expanded ? 'Hide' : 'Select Model';
		}

		if ($toggle) {
			$toggle.addEventListener('click', function () {
				setExpanded($wrap.classList.contains('is-collapsed'));
			});
		}

		function fillSelect($el, items, labelKey, valueKey, placeholder, disabled) {
			var current = $el.value;
			$el.innerHTML = '<option value="">' + HSSCore.escHtml(placeholder) + '</option>' +
				items.map(function (i) {
					return '<option value="' + HSSCore.escHtml(i[valueKey]) + '">' + HSSCore.escHtml(i[labelKey]) + '</option>';
				}).join('');
			$el.disabled = !!disabled;
			if (current && items.some(function (i) { return i[valueKey] === current; })) {
				$el.value = current;
			}
		}

		/**
		 * Model is locked out entirely until a Year is chosen — no more
		 * "every model across every year" the instant the page loads (see
		 * v1.8 note above). Always cascades into refreshTrims() so Trim's
		 * lock state stays correct too, whichever direction Year just moved.
		 */
		function refreshModels() {
			var yearSlug = $year.value;
			if (!yearSlug) {
				fillSelect($model, [], 'name', 'slug', 'Select Year first', true);
				refreshTrims();
				return;
			}
			var models = flat.models.filter(function (m) { return m.yearSlug === yearSlug; });
			fillSelect($model, models, 'name', 'slug', 'Select Model', false);
			refreshTrims();
		}

		/**
		 * Same lock as refreshModels(), one level down: no Model selected
		 * yet means Trim shows nothing to pick but a disabled placeholder.
		 * A Model with zero trim terms (a normal, supported case — some
		 * models just don't have trims) still enables the select; it simply
		 * has nothing but the placeholder option, which is fine since
		 * search filtering only ANDs in the levels that were actually set.
		 */
		function refreshTrims() {
			var modelSlug = $model.value;
			if (!modelSlug) {
				fillSelect($trim, [], 'name', 'slug', 'Select Model first', true);
				return;
			}
			var trims = flat.trims.filter(function (t) { return t.modelSlug === modelSlug; });
			fillSelect($trim, trims, 'name', 'slug', 'Select Trim', false);
		}

		function currentState() {
			return {
				year:     $year.value  ? flat.years.find(function (y) { return y.slug === $year.value; })  || { slug: $year.value, name: $year.value }  : null,
				model:    $model.value ? flat.models.find(function (m) { return m.slug === $model.value; }) || { slug: $model.value, name: $model.value } : null,
				trim:     $trim.value  ? flat.trims.find(function (t) { return t.slug === $trim.value; })  || { slug: $trim.value, name: $trim.value }  : null,
				feature:  $feature.value ? flat.features.find(function (f) { return f.slug === $feature.value; }) || { slug: $feature.value, name: $feature.value } : null,
				keywords: '',
				// No sort <select> in this UI (see [hss_video_sort]) — pass
				// whatever's currently in the URL through untouched, so
				// Submit doesn't overwrite an already-applied sort.
				sort: HSSCore.parseUrlState(config).sort
			};
		}

		function applyState(state) {
			if (state.year)  $year.value  = state.year.slug;
			refreshModels();
			if (state.model) $model.value = state.model.slug;
			refreshTrims();
			if (state.trim)    $trim.value    = state.trim.slug;
			if (state.feature) $feature.value = state.feature.slug;
		}

		/* ── Initial population ───────────────────────────────────────── */
		fillSelect($year, flat.years, 'name', 'slug', 'Select Year');
		refreshModels(); // also populates Trim via refreshTrims()
		fillSelect($feature, flat.features, 'name', 'slug', 'Feature Category');

		/* ── Restore state already present in the URL, falling back to the ──
		   FWPVFM cookie when there's nothing to read from this page's own
		   URL — this feed appears globally (e.g. homepage), so most of the
		   time there's no _model_filter param here at all. */
		applyState(HSSCore.resolveInitialState(config));

		/* ── Mobile accordion: default open (see hss-configurator.css v1.2),
		   except when a selection was just restored above — arriving with
		   Year/Model/Trim/Feature already set means there's nothing left to
		   pick, so collapse it out of the way from the start rather than
		   greeting a returning visitor with a wall of dropdowns they've
		   already filled in. */
		if ($year.value || $model.value || $trim.value || $feature.value) {
			setExpanded(false);
		}

		/* ── Cascade bindings ─────────────────────────────────────────── */
		$year.addEventListener('change', refreshModels);
		$model.addEventListener('change', refreshTrims);

		/* ── Clear ─────────────────────────────────────────────────────── */
		/**
		 * Deliberately a hard refresh rather than the soft FWP.fetch() path
		 * _navigate() normally uses. fillSelect() intentionally restores a
		 * select's previous value if it still exists in the repopulated
		 * option list — which is exactly what you want on a normal cascade
		 * (e.g. picking a different Year shouldn't silently drop a still-
		 * valid Trim) but meant Clear All never actually looked cleared:
		 * Model/Trim kept their old value since it was still a valid option
		 * once Year was blanked out. Forcing every select to '' first
		 * handles the visual part.
		 *
		 * For year/model/trim: rather than deleting FWPVFM's cookie
		 * ourselves client-side, this hands off to FWPVFM's own
		 * ?_clear_vehicle_filter=1 handler (see HSSCore.buildClearFilterUrl)
		 * — cookie deletion happens server-side on template_redirect,
		 * before any HTML ships, which is more atomic than anything we can
		 * guarantee from JS. Falls back to a plain refresh if FWPVFM isn't
		 * active on the page. Targets wherever this configurator is
		 * currently embedded rather than config.searchPage, so Clear All
		 * reloads in place instead of sending the user to the results page.
		 *
		 * For Feature Category: FWPVFM's clear endpoint only ever touches
		 * its own cookie, so it never overlapped with our separate feature
		 * cookie — clear that one directly here.
		 *
		 * For Sort: buildClearFilterUrl() (hss-core.js v1.8) strips
		 * config.paramSort from the target URL alongside the other three —
		 * nothing extra needed here since this is always a real page
		 * reload, never the FWP.fetch() soft path, so [hss_video_sort]'s
		 * <select> naturally re-renders with nothing selected.
		 */
		$clear.addEventListener('click', function () {
			$year.value = '';
			$model.value = '';
			$trim.value = '';
			$feature.value = '';
			HSSCore.clearFeatureCookie();

			var target = window.location.pathname + window.location.search;
			window.location.href = HSSCore.buildClearFilterUrl(config, target) || target;
		});

		/* ── Submit → same FacetWP bridge the chip bar uses ─────────────── */
		$submit.addEventListener('click', function () {
			var state = currentState();
			// Feature Category isn't part of FWPVFM's cookie (see
			// hss-core.js) so it needs its own sync — year/model/trim
			// don't, FWPVFM already handles those off the URL navigate()
			// is about to update.
			HSSCore.syncFeatureCookie(state);
			HSSCore.navigate(config, state);
			// Collapse the accordion now that filters are applied, freeing
			// up screen space for the results below (mobile only — no-op
			// visually on desktop, see setExpanded() above).
			setExpanded(false);
		});

		/* ── Stay in sync if FacetWP's native facets change elsewhere ───── */
		// Deliberately parseUrlState(), not resolveInitialState() — this
		// only fires when FacetWP itself is live on the page, so the URL
		// it just pushed is always authoritative; no cookie fallback needed.
		if (window.jQuery) {
			window.jQuery(document).on('facetwp-loaded', function () {
				applyState(HSSCore.parseUrlState(config));
			});
		}
	});
}());
